// Bridge `iframe` <-> parent pour les retouches IA ad-hoc (Phase 5).
//
// Le HTML rendu (CV ou lettre) est `contenteditable` dans une iframe sandboxée.
// Ce module fabrique le petit script JS embarqué dans le `<head>` du HTML pour :
//  1. signaler au parent les sélections faites par l'utilisateur, avec leur
//     texte et leurs coordonnées viewport ;
//  2. recevoir une consigne de remplacement et appliquer le texte révisé
//     SUR LA SÉLECTION mémorisée (la sélection visible peut avoir disparu
//     pendant que l'IA réfléchit).
//
// On garde le script en pure JS (pas de TS, pas de bundler) : il vit dans
// `srcdoc`, hors du build Vite.
//
// Format des messages échangés (window.postMessage) :
//   parent <- iframe  : { type: 'revision:selection', text, rect: {x,y,w,h}, hasSelection: boolean }
//   parent -> iframe  : { type: 'revision:replace', text }   -> remplace la sélection mémorisée
//   parent -> iframe  : { type: 'revision:clear' }           -> oublie la sélection mémorisée
//
// On filtre les messages par un `channel` partagé (`__cvRevisionChannel`) pour
// éviter qu'un autre script de la page LinkedIn ne nous parle par accident.

export const REVISION_CHANNEL = '__cv-revision-v1'

/**
 * Renvoie le contenu d'un `<script>` à insérer dans le `<head>` du HTML
 * rendu (CV ou lettre) pour activer le bridge de sélection.
 *
 * Pas de balises `<script>` autour — l'appelant s'en charge.
 */
export function buildRevisionBridgeScript(channel: string = REVISION_CHANNEL): string {
  // L'IIFE est volontairement compacte. Pas d'optionnel chaining (transpilé
  // partout, OK), pas de async, pas d'imports.
  return `
(function(){
  var CH = ${JSON.stringify(channel)};
  // Conserve la dernière sélection valide (Range) pour pouvoir remplacer
  // même après que l'utilisateur a cliqué dans le panneau parent.
  var lastRange = null;
  function rectOf(range) {
    try {
      var r = range.getBoundingClientRect();
      return { x: r.left, y: r.top, w: r.width, h: r.height };
    } catch (e) { return { x: 0, y: 0, w: 0, h: 0 }; }
  }
  function notify() {
    var sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      parent.postMessage({ channel: CH, type: 'revision:selection', text: '', hasSelection: false }, '*');
      return;
    }
    var range = sel.getRangeAt(0);
    var text = sel.toString();
    if (text && text.trim().length > 0) {
      lastRange = range.cloneRange();
      parent.postMessage({
        channel: CH,
        type: 'revision:selection',
        text: text,
        rect: rectOf(range),
        hasSelection: true,
      }, '*');
    } else {
      parent.postMessage({ channel: CH, type: 'revision:selection', text: '', hasSelection: false }, '*');
    }
  }
  document.addEventListener('selectionchange', notify);
  document.addEventListener('mouseup', notify);
  document.addEventListener('keyup', function(e) {
    // Filtre : ne notifie que sur les touches plausibles d'une sélection
    // (Shift+Arrow, etc.) — évite le bruit constant.
    if (e.shiftKey || e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Home' || e.key === 'End') {
      notify();
    }
  });

  window.addEventListener('message', function(ev) {
    var data = ev.data;
    if (!data || data.channel !== CH) return;
    if (data.type === 'revision:replace' && typeof data.text === 'string' && lastRange) {
      try {
        lastRange.deleteContents();
        lastRange.insertNode(document.createTextNode(data.text));
        // Reset la sélection visible APRÈS l'insertion -> évite que la
        // sélection re-couvre l'ancien texte (déjà supprimé).
        var sel = window.getSelection();
        sel.removeAllRanges();
        lastRange = null;
        parent.postMessage({ channel: CH, type: 'revision:selection', text: '', hasSelection: false }, '*');
      } catch (e) {
        parent.postMessage({ channel: CH, type: 'revision:error', message: String(e) }, '*');
      }
    } else if (data.type === 'revision:clear') {
      lastRange = null;
    }
  });
})();
`
}
